// Per-route document head for the prerendered marketing pages (/, /privacy,
// /terms). vite-react-ssg renders this through react-helmet-async and injects
// the tags into each page's static HTML at build time, so every prerendered
// route ships its OWN <title>, description, canonical and social tags.
//
// IMPORTANT: the SEO/social meta deliberately does NOT live in index.html. The
// SSG inserts helmet tags right after <head> WITHOUT removing anything already
// in the template, so a hardcoded <title>/<meta> there would produce a second,
// duplicate tag on every prerendered page. index.html keeps only the universal,
// non-duplicating bits (charset, viewport, icons, fonts); everything page-
// specific comes from here.
import { Head } from "vite-react-ssg";

const SITE_URL = "https://jobwarp.app";
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const LOGO = `${SITE_URL}/jobwarp-logo.png`;

export function Seo({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  /** Canonical path including any trailing slash, e.g. "/" or "/privacy/". */
  path: string;
}) {
  const url = `${SITE_URL}${path}`;
  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="JobWarp" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="JobWarp" />
      <meta property="og:logo" content={LOGO} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {path === "/" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "JobWarp",
            url: SITE_URL,
            logo: LOGO,
          })}
        </script>
      )}
    </Head>
  );
}
